import type { Evaluation, ListEvaluationsRequest } from '@compass/client/types/evaluation';
import type { Scorecard, ScorecardSection } from '@compass/client/types/scorecard';

import { EXPORT_PAGE_SIZE } from '@/constants/export';
import {
  computeScorecardSectionResults,
  getReviewerEffectiveFindings,
} from '@/pages/qc/qcDetail.utils';
import { getEvaluationDirection } from '@/pages/qc/utils';
import type {
  ExportConfig,
  ExportFormat,
  ExportHeader,
  ExportProgress,
} from '@/types/export.types';
import { formatCallDuration } from '@/utils/date';
import { exportData } from '@/utils/export';

import { evaluationService, scorecardService } from './api';

const BASE_QC_HEADERS: ExportHeader[] = [
  { name: 'evaluation_id', label: 'Evaluation ID', type: 'string' },
  { name: 'call_id', label: 'Call ID', type: 'string' },
  { name: 'agent', label: 'Agent Name', type: 'string' },
  { name: 'program', label: 'Program', type: 'string' },
  { name: 'direction', label: 'Call Direction', type: 'string' },
  { name: 'score', label: 'Score', type: 'number' },
  { name: 'call_start_time', label: 'Call Start Time', type: 'date' },
  { name: 'duration', label: 'Duration', type: 'string' },
  { name: 'escalation', label: 'Escalation', type: 'string' },
  { name: 'compliance', label: 'Compliance', type: 'string' },
];
const SCORE_COLUMN_INDEX = BASE_QC_HEADERS.findIndex(header => header.name === 'score');

interface EvaluationScorecardRef {
  key: string;
  organizationId: string;
  programId: string;
  scorecardId: string;
}

interface SectionScoreColumn {
  key: string;
  header: ExportHeader;
}

const normalizeKeyPart = (value?: string | null): string => {
  const trimmedValue = value?.trim();
  if (!trimmedValue) {
    return '';
  }

  return trimmedValue
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

const formatFlag = (value?: boolean): string | null => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return null;
};

const formatComplianceFlag = (value?: boolean): string | null => {
  if (value === true) return 'Fail';
  if (value === false) return 'Pass';
  return null;
};

const getMetadataStringValue = (
  metadata: Record<string, unknown> | undefined,
  keys: string[],
): string | null => {
  if (!metadata) return null;

  const normalizedEntries = Object.entries(metadata).map(
    ([k, v]) => [k.toLowerCase().replace(/[^a-z0-9]+/g, '_'), v] as const,
  );

  for (const key of keys) {
    const normalized = key.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    const match = normalizedEntries.find(([k]) => k === normalized);
    if (match && typeof match[1] === 'string' && match[1].trim()) {
      return match[1].trim();
    }
  }

  return null;
};

const resolveAgentName = (
  evaluation: Evaluation,
  agentNameById: Map<string, string>,
): string | null => {
  if (evaluation.agent_user_id) {
    const name = agentNameById.get(evaluation.agent_user_id);
    if (name) return name;
  }

  return getMetadataStringValue(evaluation.metadata as Record<string, unknown> | undefined, [
    'agent_name',
    'agent',
    'representative_name',
    'rep_name',
  ]);
};

const resolveProgramName = (
  evaluation: Evaluation,
  programNameById: Map<string, string>,
): string | null => {
  if (!evaluation.program_id) return null;
  return programNameById.get(evaluation.program_id) ?? evaluation.program_id;
};

const formatScorePercent = (score?: number | null): number | null =>
  score != null ? Math.round(score * 1000) / 10 : null;

const getEvaluationScorecardRef = (
  evaluation: Evaluation,
  fallbackOrganizationId?: string,
): EvaluationScorecardRef | null => {
  const organizationId = evaluation.organization_id ?? fallbackOrganizationId;
  const programId = evaluation.program_id;
  const scorecardId = evaluation.scorecard_id;

  if (!organizationId || !programId || !scorecardId) {
    return null;
  }

  return {
    key: [organizationId, programId, scorecardId].join('::'),
    organizationId,
    programId,
    scorecardId,
  };
};

const getSectionScoreColumnLabel = (section: ScorecardSection, index: number): string =>
  `${section.title?.trim() || `Untitled Section ${index + 1}`} Score`;

const getSectionScoreColumnKey = (
  section: ScorecardSection,
  index: number,
  scorecardRef: EvaluationScorecardRef,
): string => {
  // Deliberately key by normalized section title first so exports collapse
  // same-named sections across different scorecards into a shared column.
  const normalizedTitle = normalizeKeyPart(section.title);
  if (normalizedTitle) {
    return `section_score__${normalizedTitle}`;
  }

  const normalizedSectionId = normalizeKeyPart(section.id);
  if (normalizedSectionId) {
    return `section_score__${normalizedSectionId}`;
  }

  return `section_score__${normalizeKeyPart(scorecardRef.scorecardId)}__section_${index + 1}`;
};

const fetchScorecardsForEvaluations = async (
  evaluations: Evaluation[],
  fallbackOrganizationId?: string,
): Promise<Map<string, Scorecard>> => {
  const refsByKey = new Map<string, EvaluationScorecardRef>();

  evaluations.forEach(evaluation => {
    const scorecardRef = getEvaluationScorecardRef(evaluation, fallbackOrganizationId);
    if (!scorecardRef || refsByKey.has(scorecardRef.key)) {
      return;
    }
    refsByKey.set(scorecardRef.key, scorecardRef);
  });

  if (refsByKey.size === 0) {
    return new Map();
  }

  const results = await Promise.allSettled(
    Array.from(refsByKey.values()).map(async scorecardRef => {
      const response = await scorecardService.getScorecard({
        organization_id: scorecardRef.organizationId,
        program_id: scorecardRef.programId,
        scorecard_id: scorecardRef.scorecardId,
      });

      return { key: scorecardRef.key, scorecard: response.scorecard };
    }),
  );

  const scorecardsByKey = new Map<string, Scorecard>();

  results.forEach(result => {
    if (result.status !== 'fulfilled' || !result.value.scorecard) {
      if (result.status === 'rejected') {
        console.warn('Failed to fetch QC scorecard for export', result.reason);
      }
      return;
    }

    scorecardsByKey.set(result.value.key, result.value.scorecard);
  });

  return scorecardsByKey;
};

const buildSectionScoreColumns = (
  evaluations: Evaluation[],
  scorecardsByKey: Map<string, Scorecard>,
  fallbackOrganizationId?: string,
): SectionScoreColumn[] => {
  const columns: SectionScoreColumn[] = [];
  const seen = new Set<string>();

  evaluations.forEach(evaluation => {
    const scorecardRef = getEvaluationScorecardRef(evaluation, fallbackOrganizationId);
    if (!scorecardRef) {
      return;
    }

    const scorecard = scorecardsByKey.get(scorecardRef.key);
    scorecard?.sections?.forEach((section, index) => {
      const key = getSectionScoreColumnKey(section, index, scorecardRef);
      if (seen.has(key)) {
        return;
      }

      seen.add(key);
      columns.push({
        key,
        header: {
          name: key,
          label: getSectionScoreColumnLabel(section, index),
          type: 'number',
        },
      });
    });
  });

  return columns;
};

const buildSectionScoreValues = (
  evaluation: Evaluation,
  sectionColumns: SectionScoreColumn[],
  scorecardsByKey: Map<string, Scorecard>,
  fallbackOrganizationId?: string,
): (number | null)[] => {
  if (sectionColumns.length === 0) {
    return [];
  }

  const scorecardRef = getEvaluationScorecardRef(evaluation, fallbackOrganizationId);
  if (!scorecardRef) {
    return sectionColumns.map(() => null);
  }

  const scorecard = scorecardsByKey.get(scorecardRef.key);
  if (!scorecard) {
    return sectionColumns.map(() => null);
  }

  const sectionScoreByKey = new Map<string, number | null>();
  const effectiveFindings = getReviewerEffectiveFindings(
    evaluation.findings || [],
    evaluation.reviews?.findings,
  );
  const sectionResults = computeScorecardSectionResults(
    scorecard.sections || [],
    evaluation.findings || [],
    evaluation,
    effectiveFindings,
  );

  sectionResults.forEach((result, index) => {
    const key = getSectionScoreColumnKey(result.section, index, scorecardRef);
    sectionScoreByKey.set(key, formatScorePercent(result.score));
  });

  return sectionColumns.map(column => sectionScoreByKey.get(column.key) ?? null);
};

export const evaluationToRow = (
  evaluation: Evaluation,
  agentNameById: Map<string, string>,
  programNameById: Map<string, string>,
): (string | number | null)[] => [
  evaluation.id ?? null,
  evaluation.call_id ?? null,
  resolveAgentName(evaluation, agentNameById),
  resolveProgramName(evaluation, programNameById),
  getEvaluationDirection(evaluation) ?? null,
  formatScorePercent(evaluation.aggregate_score),
  // Call Start Time (AQ-512) — the axis the export is filtered/sorted by. Falls back to
  // created_at / evaluated_at for rows without a call start (old/un-backfilled).
  evaluation.call_start_time ?? evaluation.created_at ?? evaluation.evaluated_at ?? null,
  // Duration = call_end_time - call_start_time, formatted "5m 15s" ("—" if missing);
  // mirrors the QC list Duration column.
  formatCallDuration(evaluation.call_start_time, evaluation.call_end_time),
  formatFlag(evaluation.flags?.escalation),
  formatComplianceFlag(evaluation.flags?.compliance_failed),
];

const MAX_EXPORT_EVALUATIONS = 10_000;

/**
 * Fetches all evaluations matching filters with cursor-based pagination.
 * Capped at MAX_EXPORT_EVALUATIONS to prevent browser memory issues.
 */
export const fetchAllEvaluations = async (
  baseRequest: Omit<ListEvaluationsRequest, 'limit' | 'start_after_id'>,
  onProgress?: (progress: ExportProgress) => void,
): Promise<Evaluation[]> => {
  const allEvaluations: Evaluation[] = [];
  let startAfterId: string | undefined;
  let hasMore = true;

  while (hasMore) {
    onProgress?.({
      status: 'fetching',
      current: allEvaluations.length,
      total: 0,
      message: `Fetching evaluations (${allEvaluations.length.toLocaleString()} so far)...`,
    });

    const response = await evaluationService.listEvaluations({
      ...baseRequest,
      limit: EXPORT_PAGE_SIZE,
      start_after_id: startAfterId,
    });

    const pageEvaluations = response.evaluations ?? [];
    allEvaluations.push(...pageEvaluations);

    if (allEvaluations.length >= MAX_EXPORT_EVALUATIONS) {
      throw new Error(
        `Export limited to ${MAX_EXPORT_EVALUATIONS.toLocaleString()} evaluations. Please narrow your date range or filters.`,
      );
    }

    hasMore = !!response.next_cursor && pageEvaluations.length === EXPORT_PAGE_SIZE;
    startAfterId = response.next_cursor || undefined;
  }

  return allEvaluations;
};

export interface QcExportRequest {
  filters: Omit<ListEvaluationsRequest, 'limit' | 'start_after_id'>;
  format: ExportFormat;
  agentNameById: Map<string, string>;
  programNameById: Map<string, string>;
  onProgress?: (progress: ExportProgress) => void;
}

export const exportQcEvaluations = async (request: QcExportRequest): Promise<void> => {
  const { filters, format, agentNameById, programNameById, onProgress } = request;

  // Force the full-detail Mongo/Atlas path (findings/reviews) — the CH-backed
  // evaluation_list store doesn't carry them, and the export needs them for the
  // per-section score columns.
  const evaluations = await fetchAllEvaluations(
    { ...filters, trimmed_evaluation: false },
    onProgress,
  );

  if (evaluations.length === 0) {
    throw new Error('No Call evaluations found with the specified filters');
  }

  onProgress?.({
    status: 'processing',
    current: 0,
    total: evaluations.length,
    message: `Processing ${evaluations.length} evaluations...`,
  });

  const scorecardsByKey = await fetchScorecardsForEvaluations(evaluations, filters.organization_id);
  const sectionColumns = buildSectionScoreColumns(
    evaluations,
    scorecardsByKey,
    filters.organization_id,
  );
  const rows = evaluations.map(evaluation => {
    const baseRow = evaluationToRow(evaluation, agentNameById, programNameById);
    const sectionValues = buildSectionScoreValues(
      evaluation,
      sectionColumns,
      scorecardsByKey,
      filters.organization_id,
    );

    if (sectionValues.length === 0) {
      return baseRow;
    }

    return [
      ...baseRow.slice(0, SCORE_COLUMN_INDEX + 1),
      ...sectionValues,
      ...baseRow.slice(SCORE_COLUMN_INDEX + 1),
    ];
  });

  const config: ExportConfig = {
    headers:
      sectionColumns.length === 0
        ? BASE_QC_HEADERS
        : [
            ...BASE_QC_HEADERS.slice(0, SCORE_COLUMN_INDEX + 1),
            ...sectionColumns.map(column => column.header),
            ...BASE_QC_HEADERS.slice(SCORE_COLUMN_INDEX + 1),
          ],
    rows,
  };

  onProgress?.({
    status: 'generating',
    current: evaluations.length,
    total: evaluations.length,
    message: `Generating ${format.toUpperCase()} file...`,
  });

  const now = new Date();
  const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);

  exportData(config, {
    format,
    filename: `qc_evaluations_${timestamp}`,
    sheetName: 'QC Evaluations',
  });

  onProgress?.({
    status: 'complete',
    current: evaluations.length,
    total: evaluations.length,
    message: 'Export completed successfully',
  });
};
